import React, { forwardRef, MouseEventHandler } from 'react';
import { Button, makeStyles, Tooltip, Theme } from '@material-ui/core';
import { ButtonProps } from '@material-ui/core/Button';
import PencilIcon from '@axes4/react-icons/jsx/Pencil';
import type { TFunctionResult } from 'i18next';

const useStyles = makeStyles<Theme, EditCardButtonWrapperProps>(theme => ({
    button: {
        position: 'absolute',
        padding: theme.spacing(1),
        right: theme.spacing(2),
        top: -theme.spacing(6),
        minWidth: 'auto',
        '& > span > span': {
            margin: 0,
        },
    },
    label: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        opacity: 0,
    },
    wrapper: {
        position: 'relative',
        margin: props => props.isCardContent ? -16 : null,
    },
}));

type EditCardButtonWrapperProps = {
    isCardContent?: boolean;
};

/**
 * Wrapper around the EditCard Button for better positioning
 */
export const EditCardButtonWrapper: React.FunctionComponent<EditCardButtonWrapperProps> = props => {
    const classes = useStyles(props);
    return <div className={classes.wrapper}>{props.children}</div>;
};

export interface EditCardButtonProps {
    buttonProps?: ButtonProps;
    icon?: React.ReactNode;
    label: string|TFunctionResult;
    tooltip?: string;
    onClick: MouseEventHandler;
}

export const EditCardButton = forwardRef<HTMLAnchorElement|HTMLButtonElement, EditCardButtonProps>((props, ref) => {
    const icon = props.icon || <PencilIcon/>;
    const classes = useStyles({});

    const button = (
        <Button
            variant="outlined"
            color="primary"
            ref={ref}
            startIcon={icon}
            {...props.buttonProps}
            className={classes.button}
            onClick={props.onClick}
            aria-label={props.label}
        >
            <span className={classes.label}>{props.label}</span>
        </Button>
    );

    if (props.tooltip) {
        return <Tooltip title={props.tooltip}>{button}</Tooltip>;
    }

    return button;
});
EditCardButton.displayName = 'EditCardButton';
